<template>
  <div class="st-page">
    <Navigation />
    <div class="container" style="padding-top:20px;max-width:1100px;">
      <div class="d-flex align-items-center justify-content-between mb-3 flex-wrap gap-2">
        <div class="d-flex align-items-center gap-3">
          <a href="javascript:void(0)" @click="$router.push('/settings')" class="st-back"><i class="mdi mdi-arrow-left"></i></a>
          <h4 class="st-page-title"><i class="mdi mdi-bell-cog me-2"></i>Notification Settings</h4>
        </div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="btn st-btn-outline btn-sm" @click="loadCatalog" :disabled="loading">
            <span v-if="loading" class="spinner-border spinner-border-sm me-1"></span>🔄
          </button>
          <button class="btn st-btn-primary btn-sm" @click="saveAll" :disabled="saving || !dirty">
            <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>💾 Save Changes
          </button>
        </div>
      </div>

      <div class="st-info-box mb-3" style="padding:12px;font-size:12px;color:#8BA4C4;">
        Control which notifications get sent via SMS and email for your institution. <strong style="color:#E8EDF4;">Critical events</strong>
        (OTPs, password resets) are always on and cannot be disabled — these are needed for users to access the system safely.
        Each SMS roughly costs <strong style="color:#F59E0B;">KSh 1.60</strong> per 160 characters — disable the receipts you don't need.
      </div>

      <!-- Headline counters -->
      <div class="pm-grid">
        <div class="pm-card" style="border-left-color:#10B981;">
          <div class="pm-card-label">SMS Enabled</div>
          <div class="pm-card-value" style="color:#10B981;">{{ enabledCount.sms }}</div>
          <div class="pm-card-sub">of {{ totalNonCritical }} optional events</div>
        </div>
        <div class="pm-card" style="border-left-color:#4FC3F7;">
          <div class="pm-card-label">Email Enabled</div>
          <div class="pm-card-value" style="color:#4FC3F7;">{{ enabledCount.email }}</div>
          <div class="pm-card-sub">of {{ totalNonCritical }} optional events</div>
        </div>
        <div class="pm-card" style="border-left-color:#EF4444;">
          <div class="pm-card-label">Critical (Locked)</div>
          <div class="pm-card-value" style="color:#EF4444;">{{ criticalCount }}</div>
          <div class="pm-card-sub">always on (OTPs, etc.)</div>
        </div>
      </div>

      <!-- Quick presets -->
      <div class="st-section mb-3" style="padding:14px;">
        <div style="font-size:11px;color:#4A6A8A;text-transform:uppercase;font-weight:600;margin-bottom:6px;">Quick Presets</div>
        <div class="d-flex gap-2 flex-wrap">
          <button class="btn st-btn-outline btn-sm" @click="applyPreset('lean')">🪙 Lean (Auth only)</button>
          <button class="btn st-btn-outline btn-sm" @click="applyPreset('standard')">⚖️ Standard (Receipts off)</button>
          <button class="btn st-btn-outline btn-sm" @click="applyPreset('full')">📢 Everything On</button>
        </div>
      </div>

      <!-- Per-category tables -->
      <div v-for="cat in groupedCatalog" :key="cat.name" class="st-section mb-3" style="padding:14px;">
        <div style="font-size:13px;font-weight:600;color:#4FC3F7;margin-bottom:10px;">{{ cat.name }}</div>
        <table class="pm-table">
          <thead><tr>
            <th>Event</th>
            <th class="text-center" style="min-width:90px;">SMS</th>
            <th class="text-center" style="min-width:90px;">Email</th>
            <th>Note</th>
          </tr></thead>
          <tbody>
            <tr v-for="r in cat.items" :key="r.key">
              <td>
                <div style="color:#E8EDF4;font-weight:600;">{{ r.label }}</div>
                <div style="font-size:11px;color:#4A6A8A;font-family:monospace;">{{ r.key }}</div>
              </td>
              <td class="text-center">
                <label class="toggle">
                  <input type="checkbox" v-model="r.sms_enabled" :disabled="r.critical" @change="markDirty(r)" />
                  <span class="slider" :class="r.critical ? 'locked' : ''"></span>
                </label>
              </td>
              <td class="text-center">
                <label class="toggle">
                  <input type="checkbox" v-model="r.email_enabled" :disabled="r.critical" @change="markDirty(r)" />
                  <span class="slider" :class="r.critical ? 'locked' : ''"></span>
                </label>
              </td>
              <td style="font-size:11px;color:#8BA4C4;">
                <span v-if="r.critical" style="color:#EF4444;">🔒 Critical — always on</span>
                <span v-else-if="r.overridden">Customised</span>
                <span v-else style="color:#4A6A8A;">Default</span>
              </td>
            </tr>
          </tbody>
        </table>
      </div>

      <div v-if="dirty" style="position:sticky;bottom:10px;text-align:right;">
        <button class="btn st-btn-primary btn-sm" @click="saveAll" :disabled="saving">
          <span v-if="saving" class="spinner-border spinner-border-sm me-1"></span>💾 Save Changes
        </button>
      </div>
    </div>
  </div>
</template>

<script>
/* eslint-disable */
import Navigation from "@/components/simi-auth/Navigation.vue";
import { executeJson, simitrack } from "@/api";
export default {
  name: 'NotificationSettings',
  components: { Navigation },
  data() {
    return {
      loading: false,
      saving: false,
      catalog: [],
      dirty: false,
    };
  },
  computed: {
    groupedCatalog() {
      const groups = {};
      this.catalog.forEach(c => { (groups[c.category] = groups[c.category] || []).push(c); });
      // Preserve a sensible order
      const order = ['Auth', 'Onboarding', 'Receipts', 'Sponsors', 'Behaviour', 'Billing', 'Marketing', 'Other'];
      return order
        .filter(n => groups[n])
        .map(n => ({ name: n, items: groups[n] }))
        .concat(Object.keys(groups).filter(k => !order.includes(k)).map(k => ({ name: k, items: groups[k] })));
    },
    enabledCount() {
      const sms = this.catalog.filter(c => !c.critical && c.sms_enabled).length;
      const email = this.catalog.filter(c => !c.critical && c.email_enabled).length;
      return { sms, email };
    },
    totalNonCritical() { return this.catalog.filter(c => !c.critical).length; },
    criticalCount() { return this.catalog.filter(c => c.critical).length; },
  },
  mounted() { this.loadCatalog(); },
  methods: {
    async loadCatalog() {
      this.loading = true;
      try {
        const res = await executeJson({ function: 'getNotificationCatalog' }, simitrack);
        if (res.data.success) {
          this.catalog = (res.data.data.catalog || []).map(c => ({
            ...c,
            sms_enabled: !!c.sms_enabled,
            email_enabled: !!c.email_enabled,
          }));
          this.dirty = false;
        }
      } catch (e) {}
      this.loading = false;
    },
    markDirty() { this.dirty = true; },
    async saveAll() {
      this.saving = true;
      try {
        const preferences = this.catalog
          .filter(c => !c.critical)
          .map(c => ({ event_type: c.key, sms_enabled: c.sms_enabled ? 1 : 0, email_enabled: c.email_enabled ? 1 : 0 }));
        const res = await executeJson({ function: 'bulkUpdateNotificationPreferences', preferences: JSON.stringify(preferences) }, simitrack);
        if (res.data.success) {
          this.dirty = false;
          this.loadCatalog();
        } else {
          alert(res.data.message);
        }
      } catch (e) {
        alert(e.response?.data?.message || e.message);
      }
      this.saving = false;
    },
    applyPreset(kind) {
      // 'lean' = critical only (everything else off)
      // 'standard' = critical + onboarding + sponsors + billing on; receipts/marketing/behaviour off
      // 'full' = everything on
      this.catalog.forEach(c => {
        if (c.critical) return;
        if (kind === 'lean') { c.sms_enabled = false; c.email_enabled = false; }
        else if (kind === 'full') { c.sms_enabled = true; c.email_enabled = true; }
        else if (kind === 'standard') {
          const offSms = ['pos_payment', 'wallet_topup', 'guest_receipt', 'order_ready', 'bulk_sms', 'chore_completed', 'parent_login'];
          c.sms_enabled = !offSms.includes(c.key);
          c.email_enabled = ['user_created', 'onboarding', 'parent_added', 'parent_added_resend', 'fee_payment', 'payment_reversed', 'sponsor_settlement', 'sponsor_portal', 'platform_invoice', 'parent_invoice'].includes(c.key);
        }
      });
      this.dirty = true;
    },
  },
};
</script>

<style scoped>
.st-page{min-height:100vh;background:#0A1628;padding-top:70px;}
.st-back{color:#8BA4C4;font-size:22px;text-decoration:none;padding:4px;}.st-back:hover{color:#4FC3F7;}
.st-page-title{color:#E8EDF4;font-size:18px;font-weight:700;margin:0;}
.st-section{background:#0F2347;border:1px solid #1A3A6A;border-radius:14px;}
.st-btn-primary{background:linear-gradient(135deg,#2E86DE,#1A6BC4);color:#fff;border:none;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}
.st-btn-primary:disabled{opacity:.6;}
.st-btn-outline{background:transparent;border:1px solid #1A3A6A;color:#8BA4C4;font-weight:600;border-radius:8px;padding:8px 16px;font-size:13px;}
.st-btn-outline:hover{border-color:#4FC3F7;color:#4FC3F7;}
.st-info-box{background:#0A1628;border:1px solid #1A3A6A;border-radius:10px;}

.pm-grid{display:grid;grid-template-columns:repeat(auto-fit,minmax(200px,1fr));gap:10px;margin-bottom:16px;}
.pm-card{background:#0F2347;border:1px solid #1A3A6A;border-radius:10px;padding:14px;border-left:3px solid #1A3A6A;}
.pm-card-label{font-size:10px;color:#4A6A8A;text-transform:uppercase;font-weight:600;letter-spacing:.5px;}
.pm-card-value{font-size:24px;font-weight:800;margin:2px 0;}
.pm-card-sub{font-size:11px;color:#8BA4C4;}

.pm-table{width:100%;border-collapse:collapse;font-size:12px;color:#E8EDF4;}
.pm-table thead tr{background:#0A1628;}
.pm-table th{padding:8px;color:#4FC3F7;font-weight:600;text-align:left;font-size:10px;text-transform:uppercase;border-bottom:2px solid #1A3A6A;white-space:nowrap;}
.pm-table td{padding:8px;border-bottom:1px solid #1A3A6A;vertical-align:middle;}
.text-center{text-align:center;}

/* Toggle switch */
.toggle{position:relative;display:inline-block;width:42px;height:22px;}
.toggle input{opacity:0;width:0;height:0;}
.slider{position:absolute;cursor:pointer;top:0;left:0;right:0;bottom:0;background:#1A3A6A;border-radius:22px;transition:.2s;}
.slider:before{position:absolute;content:"";height:16px;width:16px;left:3px;top:3px;background:#8BA4C4;border-radius:50%;transition:.2s;}
.toggle input:checked + .slider{background:#10B981;}
.toggle input:checked + .slider:before{transform:translateX(20px);background:#fff;}
.slider.locked{background:#4A2929 !important;cursor:not-allowed;}
.toggle input:checked + .slider.locked{background:#7F1D1D !important;}
.toggle input:disabled + .slider:before{background:#666;}
</style>
